import type { SDKTokenResponseSchema } from '@/api/generated/schemas';
import { captureException } from '@sentry/react';
import { SDK_TOKEN } from '@/constants/queries';

interface TokenCacheEntry {
  data: SDKTokenResponseSchema;
  expiresAt: string; // ISO timestamp
  fetchedAt: string; // ISO timestamp
}

export const TOKEN_STORAGE_KEY = 'alc_thoughtspot_token';
export const TOKEN_LIFETIME_MS = SDK_TOKEN.LIFETIME_MS;
export const TOKEN_REFRESH_THRESHOLD_MS = SDK_TOKEN.REFRESH_THRESHOLD_MS;

class TokenStorage {
  private getEntry(): TokenCacheEntry | null {
    try {
      const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      if (!stored) return null;

      const entry: TokenCacheEntry = JSON.parse(stored);

      // Validate structure
      if (!entry.data?.sdkToken || !entry.expiresAt || !entry.fetchedAt) {
        this.clearToken();
        return null;
      }

      return entry;
    } catch (error) {
      captureException(error, {
        level: 'error',
        extra: {
          message: '[TokenStorage] Failed to parse token from sessionStorage',
        },
      });
      this.clearToken();
      return null;
    }
  }

  setToken(data: SDKTokenResponseSchema): void {
    try {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + TOKEN_LIFETIME_MS);

      const entry: TokenCacheEntry = {
        data,
        expiresAt: expiresAt.toISOString(),
        fetchedAt: now.toISOString(),
      };

      sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(entry));
    } catch (error) {
      captureException(error, {
        level: 'error',
        extra: {
          message: '[TokenStorage] Failed to store token in sessionStorage',
        },
      });
    }
  }

  getToken(): SDKTokenResponseSchema | null {
    const entry = this.getEntry();
    if (!entry) return null;

    // Check if expired
    if (!this.isTokenValid()) {
      this.clearToken();
      return null;
    }

    return entry.data;
  }

  isTokenValid(): boolean {
    const entry = this.getEntry();
    if (!entry) return false;

    const expiresAt = new Date(entry.expiresAt);
    const now = new Date();

    return now < expiresAt;
  }

  getTimeUntilExpiry(): number {
    const entry = this.getEntry();
    if (!entry) return 0;

    const expiresAt = new Date(entry.expiresAt);
    const now = new Date();

    return Math.max(0, expiresAt.getTime() - now.getTime());
  }

  needsRefresh(): boolean {
    const timeLeft = this.getTimeUntilExpiry();
    return timeLeft > 0 && timeLeft < TOKEN_REFRESH_THRESHOLD_MS;
  }

  clearToken(): void {
    try {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch (error) {
      captureException(error, {
        level: 'error',
        extra: {
          message: '[TokenStorage] Failed to clear token from sessionStorage',
        },
      });
    }
  }

  getFetchedAt(): Date | null {
    const entry = this.getEntry();
    if (!entry) return null;
    return new Date(entry.fetchedAt);
  }
}

export const tokenStorage = new TokenStorage();
